import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedNumber } from "react-intl";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { startCase } from "lodash-es";
import Breadcrumbs from "@components/shared/Breadcrumbs/Breadcrumbs";
import OwButton from "@components/shared/OwButton/OwButton";
import { OwFnTable } from "@components/shared/OwFnTable/OwFnTable";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";
import PaginationBar from "@components/shared/PaginationBar/PaginationBar";
import { useAppContextNonNull } from "@contexts/AppContext";
import IOnGetInventoryLogsDTO, { IInventoryLogEntry } from "@models/DTO/IOnGetInventoryLogsDTO";
import InventoryOperationEnum from "@models/InventoryOperationEnum";
import IOwTableCols from "@models/IOwTableCols";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import InventoryService, { InventoryLogAccessError, InventoryLogItemNotFoundError } from "@services/InventoryService";
import UIService from "@services/UIService";
import { IInventoryLogFilterQuery, INVENTORY_LOG_OPERATION_LABELS, INVENTORY_LOG_SCOPE_PARAMS } from "@config/filters/inventoryLogFilters";
import { canViewInventoryLog } from "@utils/inventoryLogLinks";
import { InventoryItemLogFilterBar, INVENTORY_LOG_FILTER_ACTIONS_ID } from "./InventoryItemLogFilterBar/InventoryItemLogFilterBar";
import {
    formatBeforeAfter,
    formatLogDate,
    formatLogTimestamp,
    formatSignedChange,
    inventoryLogTimezone,
    signedLogChange,
} from "./inventoryLogFormat";

/** The API caps a page at 100 entries. */
const LIMIT_OPTIONS = [10, 25, 50, 100];
const DEFAULT_LIMIT = 25;

/**
 * One catalog item's inventory log (OR-1555): /inventory/items/:catalogItemGuid, newest first, paged, filtered with
 * the shared FilterBar. The "View inventory log" links on order and PO lines add order_guid / po_guid, which narrow
 * the log to that order or PO and show as a banner with a way back to every entry.
 */
export const InventoryItemLog = () => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();
    const { catalogItemGuid = "" } = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const orderGuid = searchParams.get("order_guid") || "";
    const poGuid = searchParams.get("po_guid") || "";

    const scopeParams = useMemo(() => {
        const params: { [param: string]: string } = {};

        if (orderGuid) {
            params.order_guid = orderGuid;
        }

        if (poGuid) {
            params.po_guid = poGuid;
        }

        return params;
    }, [orderGuid, poGuid]);

    // Null until the FilterBar's mount search hands over the first query.
    const [filters, setFilters] = useState<IInventoryLogFilterQuery | null>(null);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    const [result, setResult] = useState<IOnGetInventoryLogsDTO | null>(null);
    const [isFetching, setIsFetching] = useState(false);
    const [notFound, setNotFound] = useState(false);
    // The API refuses other roles too; checking here first skips a request that can only fail.
    const [noAccess, setNoAccess] = useState(() => !canViewInventoryLog(userModel, supplierModel));
    const requestSeq = useRef(0);

    // A different scope (Show all entries, or Back / Forward) starts again from the first page.
    const scopeKey = `${orderGuid}|${poGuid}`;
    const [pagedScopeKey, setPagedScopeKey] = useState(scopeKey);

    if (pagedScopeKey !== scopeKey) {
        setPagedScopeKey(scopeKey);
        setPage(0);
    }

    useEffect(() => {
        if (filters === null || !catalogItemGuid || pagedScopeKey !== scopeKey || noAccess) {
            return;
        }

        const seq = ++requestSeq.current;

        setIsFetching(true);

        InventoryService.getInventoryLogs(
            { catalogItemGuid, page, limit, filters: { ...(filters as Record<string, unknown>), ...scopeParams } },
            userModel,
            supplierModel
        )
            .then((data) => {
                if (seq === requestSeq.current) {
                    setResult(data);
                    setNotFound(false);
                }
            })
            .catch((error) => {
                if (seq !== requestSeq.current) {
                    return;
                }

                if (error instanceof InventoryLogItemNotFoundError) {
                    setNotFound(true);
                    setResult(null);

                    return;
                }

                if (error instanceof InventoryLogAccessError) {
                    setNoAccess(true);
                    setResult(null);

                    return;
                }

                console.error("Failed to load the inventory log", error);
                uiContext.notifyFn(null, { message: "inventory_log_load_failed", level: NotificationLevelEnum.error });
            })
            .finally(() => {
                if (seq === requestSeq.current) {
                    setIsFetching(false);
                }
            });
        // userModel / supplierModel / uiContext are stable for the page's life; a new object would only refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, page, limit, scopeParams, catalogItemGuid, pagedScopeKey, scopeKey, noAccess]);

    /** FilterBar search (mount, Search, Refresh, preset tabs): back to the first page with the new query. */
    const handleSearch = useCallback((query: IInventoryLogFilterQuery) => {
        setFilters({ ...query });
        setPage(0);
    }, []);

    const onPaginationChanged = useCallback((nextPage: number, nextLimit: number) => {
        setPage(nextPage);
        setLimit(nextLimit);
    }, []);

    /** Drop the order / PO scope, keeping the filters. */
    const showAllEntries = () => {
        const next = new URLSearchParams(location.search);

        INVENTORY_LOG_SCOPE_PARAMS.forEach((param) => next.delete(param));

        const search = next.toString();

        navigate({ pathname: location.pathname, search: search ? `?${search}` : "" });
    };

    const timezone = inventoryLogTimezone(userModel.pref_timezone);
    const catalogItem = result?.catalogItem;
    const showHeld = !!supplierModel.auto_hold_for_backorders || (catalogItem?.qtyHeldForBackorders || 0) > 0;
    const isLight = UIService.isMainViewLight();

    const columns = useMemo((): IOwTableCols[] => {
        const cols: IOwTableCols[] = [
            {
                title: "date",
                valueProp: "createdAt",
                renderFnEmitsRow: true,
                className: "text-nowrap",
                renderFn: (row: IInventoryLogEntry) => formatLogTimestamp(row.createdAt, timezone),
            },
            {
                title: "operation",
                valueProp: "operationId",
                renderFnEmitsRow: true,
                renderFn: (row: IInventoryLogEntry) => {
                    const labelId = INVENTORY_LOG_OPERATION_LABELS[Number(row.operationId) as InventoryOperationEnum];

                    return (
                        <div>
                            {labelId ? <OwTranslate id={labelId} defaultMessage={startCase(labelId)} /> : null}
                            {row.cycleCountSubtype ? <div className={"small text-muted"}>{startCase(row.cycleCountSubtype)}</div> : null}
                        </div>
                    );
                },
            },
            {
                title: "change",
                valueProp: "qty",
                renderFnEmitsRow: true,
                className: "text-end",
                renderFn: (row: IInventoryLogEntry) => {
                    const change = signedLogChange(row);

                    return (
                        <span className={change > 0 ? "text-success" : change < 0 ? "text-danger" : ""}>{formatSignedChange(change)}</span>
                    );
                },
            },
            {
                title: "on_hand",
                valueProp: "endQtyOnHand",
                renderFnEmitsRow: true,
                className: "text-nowrap",
                renderFn: (row: IInventoryLogEntry) => formatBeforeAfter(row.startQtyOnHand, row.endQtyOnHand),
            },
            {
                title: "reserved",
                valueProp: "endReservedQtyOnHand",
                renderFnEmitsRow: true,
                className: "text-nowrap",
                renderFn: (row: IInventoryLogEntry) => formatBeforeAfter(row.startReservedQtyOnHand, row.endReservedQtyOnHand),
            },
        ];

        if (showHeld) {
            cols.push({
                title: "held",
                valueProp: "endQtyHeld",
                renderFnEmitsRow: true,
                className: "text-nowrap",
                renderFn: (row: IInventoryLogEntry) => formatBeforeAfter(row.startQtyHeld, row.endQtyHeld),
            });
        }

        cols.push(
            {
                title: "order",
                valueProp: "order",
                renderFnEmitsRow: true,
                renderFn: (row: IInventoryLogEntry) =>
                    row.order ? <Link to={`/orders/${row.order.guid}/entry`}>{row.order.name || row.order.guid}</Link> : null,
            },
            {
                title: "purchase_order",
                valueProp: "purchaseOrder",
                renderFnEmitsRow: true,
                renderFn: (row: IInventoryLogEntry) => {
                    if (!row.purchaseOrder) {
                        return null;
                    }

                    const receivedDate = formatLogDate(row.poShipment?.receivedDate || null, timezone);

                    return (
                        <div>
                            <Link to={`/purchase_orders/${row.purchaseOrder.guid}`}>
                                {row.purchaseOrder.name || row.purchaseOrder.guid}
                            </Link>
                            {receivedDate ? (
                                <div className={"small text-muted text-nowrap"}>
                                    <OwTranslate id={"received"} defaultMessage={"Received"} /> {receivedDate}
                                </div>
                            ) : null}
                        </div>
                    );
                },
            }
        );

        if (supplierModel.has_warehouses) {
            cols.push({
                title: "warehouse",
                valueProp: "warehouse",
                renderFnEmitsRow: true,
                renderFn: (row: IInventoryLogEntry) => row.warehouse?.name || null,
            });
        }

        cols.push(
            {
                title: "user",
                valueProp: "userName",
                renderFnEmitsRow: true,
                renderFn: (row: IInventoryLogEntry) => row.userName || null,
            },
            {
                title: "note",
                valueProp: "note",
                renderFnEmitsRow: true,
                renderFn: (row: IInventoryLogEntry) => <div className={"text-break"}>{row.note}</div>,
            }
        );

        return cols;
    }, [timezone, showHeld, supplierModel.has_warehouses]);

    const limitArr = useMemo(() => LIMIT_OPTIONS.map((value) => ({ value, isSelected: value === limit })), [limit]);

    const renderScopeBanner = () => {
        if (!orderGuid && !poGuid) {
            return null;
        }

        const scope = result?.scope;
        let message = (
            <OwTranslate id={"inventory_log_scope_not_found"} defaultMessage={"The linked order or purchase order was not found"} />
        );

        // Only a result for this scope names it: right after a scope change the previous result is still showing.
        if (orderGuid && scope?.order?.guid === orderGuid) {
            message = (
                <OwTranslate
                    id={"inventory_log_scope_order"}
                    defaultMessage={"Showing entries for order {name}"}
                    values={{ name: <strong>{scope.order.name}</strong> }}
                />
            );
        } else if (poGuid && scope?.purchaseOrder?.guid === poGuid) {
            message = (
                <OwTranslate
                    id={"inventory_log_scope_purchase_order"}
                    defaultMessage={"Showing entries for purchase order {name}"}
                    values={{ name: <strong>{scope.purchaseOrder.name}</strong> }}
                />
            );
        } else if (!result || isFetching) {
            // Still loading (a stale result from before a scope change counts too): say nothing rather than "not found".
            message = <span />;
        }

        return (
            <div
                className={"alert alert-info d-flex flex-wrap align-items-center justify-content-between gap-2 py-2"}
                data-testid={"inventory-log-scope"}
            >
                <div>
                    <i className={`fad ${poGuid ? "fa-file-invoice" : "fa-receipt"} me-2`} />
                    {message}
                </div>
                <OwButton
                    className={"btn-secondary btn-sm"}
                    title={"show_all_entries"}
                    fn={showAllEntries}
                    testId={"inventory-log-show-all"}
                />
            </div>
        );
    };

    const renderStockSummary = () => {
        if (!catalogItem) {
            return null;
        }

        const stats: { title: string; value: number }[] = [
            { title: "on_hand", value: catalogItem.qtyOnHand },
            { title: "reserved", value: catalogItem.reservedQtyOnHand },
            ...(showHeld ? [{ title: "held_for_backorders", value: catalogItem.qtyHeldForBackorders }] : []),
            { title: "backordered", value: catalogItem.qtyBackordered },
            { title: "incoming", value: catalogItem.qtyIncoming },
        ];

        return (
            <div className={`card my-3 ${isLight ? "" : "bg-dark text-light"}`}>
                <div className={"card-body d-flex flex-wrap align-items-center gap-4"}>
                    <div className={"me-auto"}>
                        <div className={"fw-bold"}>{catalogItem.name}</div>
                        {catalogItem.sku ? (
                            <div className={"small text-muted"}>
                                <OwTranslate id={"sku"} defaultMessage={"SKU"} />: {catalogItem.sku}
                            </div>
                        ) : null}
                    </div>
                    {stats.map((stat) => (
                        <div key={stat.title} className={"text-center"}>
                            <OwLabel title={stat.title} noBottomMargin={true} className={"small text-muted"} />
                            <div className={"fs-5"}>
                                <FormattedNumber value={stat.value} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderBody = () => {
        if (notFound) {
            return (
                <div className={"alert alert-warning my-3"} data-testid={"inventory-log-not-found"}>
                    <OwTranslate id={"inventory_log_item_not_found"} defaultMessage={"This item was not found in your catalogs."} />{" "}
                    <Link to={"/inventory"}>
                        <OwTranslate id={"inventory"} defaultMessage={"Inventory"} />
                    </Link>
                </div>
            );
        }

        const logs = result?.logs || [];

        return (
            <>
                {renderStockSummary()}

                <OwFnTable
                    id={"inventory-log-table"}
                    columns={columns}
                    dataSet={logs}
                    dataFetch={isFetching}
                    initialData={result === null}
                />

                {result && !isFetching && logs.length === 0 ? (
                    <div className={"text-center text-muted my-4"} data-testid={"inventory-log-empty"}>
                        <OwTranslate id={"no_inventory_log_entries"} defaultMessage={"No inventory log entries match these filters."} />
                    </div>
                ) : null}

                <div className={"mt-2"}>
                    <PaginationBar
                        offset={page}
                        limit={limit}
                        totalRecords={result?.totalCount || 0}
                        onPaginationChanged={onPaginationChanged}
                        limitArr={limitArr}
                    />
                </div>
            </>
        );
    };

    return (
        <div className={"InventoryItemLog mt-2"}>
            <Breadcrumbs
                crumbs={[
                    { title: "inventory", url: "/inventory" },
                    { title: "inventory_log", url: `/inventory/items/${catalogItemGuid}` },
                ]}
            />

            <div className={"d-flex align-items-center"}>
                <OwTitle leftIconName={"fad fa-clipboard-list"} title={"inventory_log"} tag={"h2"} />
                <div id={INVENTORY_LOG_FILTER_ACTIONS_ID} className={"ms-auto d-flex align-items-center d-print-none"} />
            </div>

            {noAccess ? (
                <div className={"alert alert-warning my-3"} data-testid={"inventory-log-no-access"}>
                    <OwTranslate
                        id={"inventory_log_no_access"}
                        defaultMessage={"Inventory logs are available to Admins, Sales users with full access and Inventory Mgmt."}
                    />
                </div>
            ) : (
                <>
                    {renderScopeBanner()}

                    <div className={"mb-2"}>
                        <InventoryItemLogFilterBar
                            catalogItemGuid={catalogItemGuid}
                            scopeParams={scopeParams}
                            isSearching={isFetching}
                            searchFn={handleSearch}
                        />
                    </div>

                    {renderBody()}
                </>
            )}
        </div>
    );
};

export default InventoryItemLog;
